import React from 'react';
import Text from 'antd/lib/typography/Text';
import { useHistory } from 'react-router-dom';
import { useSidebarColors, useSidebarTheme } from '../styles/themeMode';
import { AppComponentProps } from '../../typescriptHelpers';
import {
    BlockOutlined,
    CalendarOutlined,
    AppstoreAddOutlined,
    CaretRightOutlined,
    MobileOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectBuildDistributionBucketsForAppId,
    selectIsBuildDistroEnabled,
    selectAppSettings,
} from '../../selectors/selectors';
import {
    createAppBuildsRoute,
    createAppReleasesRoute,
    createAppBuildDistroBucketRoute,
    createAppSharedWithMeBucketRoute,
    createAppReleaseCalendarRoute,
    ROUTER_REDIRECT,
} from '../../helpers/routeHelpers';
import Collapse from 'antd/lib/collapse';
import Releases from '../Releases';
import { NavigationSegmented } from './shared/components';
import { BucketStatus } from '../../types';
import { BucketAccessIconTooltip, BuildBucketAvatar } from '../buildDistribution/CommonComponents';
import { buildBucketSort, getIsBuildDistroSupportedPlatform } from '../../helpers';
import { useAppPlatform } from '@/utils/hooks/helpers';
import {
    useAppNavigationTabContext,
    NavigationTab,
    SUB_OPTION_RELEASE_CALENDAR,
    SUB_OPTION_SHARED_WITH_ME,
} from '@/utils/hooks/useAppNavigationTabContext';
import { useIsMobile } from '@/mediaQueriesHelpers';

// Re-export for external consumers
export { NavigationTab } from '@/utils/hooks/useAppNavigationTabContext';

export type SubMenuOption = string;

export const displayTextForNavigationTab = (option: NavigationTab) => {
    switch (option) {
        case NavigationTab.Releases:
            return 'Releases';
        case NavigationTab.Builds:
            return 'Builds';
    }
};

export const routeForNavigationTab = (option: NavigationTab, orgId: string, appId: string) => {
    switch (option) {
        case NavigationTab.Builds:
            return createAppBuildsRoute(orgId, appId);
        case NavigationTab.Releases:
            return createAppReleasesRoute(orgId, appId);
    }
};

const menuItemContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
};

export const MenuItem: React.FC<{
    title: React.ReactNode;
    icon: React.ReactNode;
    shouldBeOpenByDefault?: boolean;
    isSelected: boolean;
    onClick: () => void;
    isChildMenuItem?: boolean;
    content?: React.ReactNode;
}> = ({ title, icon, content, onClick, isSelected, shouldBeOpenByDefault, isChildMenuItem }) => {
    const sc = useSidebarColors();
    const [isOpen, setIsOpen] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);

    const itemStyle: React.CSSProperties = {
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    };

    React.useEffect(() => {
        if (shouldBeOpenByDefault) {
            setIsOpen(true);
        }
    }, [shouldBeOpenByDefault]);

    const getBackgroundColor = () => {
        if (isChildMenuItem && isSelected) return sc.menuActiveBg;
        if (isChildMenuItem && isHovered) return sc.menuHoverBg;
        return 'transparent';
    };

    const titleComponent = (
        <div
            style={{
                margin: 0,
                padding: '10px 18px',
                backgroundColor: getBackgroundColor(),
                transition: 'background-color 0.2s',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => {
                if (!isOpen) {
                    setIsOpen(true);
                }
                onClick();
            }}
        >
            {typeof title === 'string' ?
                <div
                    style={{
                        paddingLeft: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 22,
                    }}
                >
                    {icon}
                    <Text style={{ fontSize: 16, fontWeight: 600, color: sc.text }}>{title}</Text>
                </div>
            :   title}
        </div>
    );

    const collapseWrapper = (children: React.ReactNode) => (
        <Collapse
            defaultActiveKey={shouldBeOpenByDefault ? ['1'] : []}
            activeKey={isOpen ? '1' : undefined}
            ghost
            expandIconPosition="end"
            collapsible="icon"
            style={{
                padding: 0,
                margin: 0,
            }}
            expandIcon={({ isActive }) => (
                <CaretRightOutlined
                    style={{
                        color: sc.textMuted,
                        fontSize: 14,
                    }}
                    rotate={isActive ? 90 : 0}
                />
            )}
            destroyInactivePanel={false}
            onChange={() => setIsOpen(!isOpen)}
            className={isSelected ? 'menu-item-collapse-selected' : 'menu-item-collapse'}
            items={[
                {
                    key: '1',
                    label: titleComponent,
                    children: children,
                    headerClass:
                        isSelected ? 'app-menu-item-selected'
                        : isChildMenuItem ? 'app-menu-item-child'
                        : 'app-menu-item',
                    forceRender: true,
                    style: {
                        padding: 0,
                        ...itemStyle,
                    },
                },
            ]}
        />
    );

    return content ? collapseWrapper(content) : titleComponent;
};

export const AppNavigationContent: React.FC<AppComponentProps> = ({ appId, orgId }) => {
    const history = useHistory();
    const isMobile = useIsMobile();
    const sc = useSidebarColors();
    const sidebarTheme = useSidebarTheme();

    const menuItemTextStyle: React.CSSProperties = { fontSize: 14, fontWeight: 400, color: sc.text };
    const menuItemIconStyle: React.CSSProperties = { color: sc.icon, fontSize: 12, width: 19 };

    // Use centralized tab context for route-based tab selection
    const { routeBasedTab, selectedSubOption } = useAppNavigationTabContext();

    const appBuckets = useSelector((state: RootState) => selectBuildDistributionBucketsForAppId(state, appId)) ?? [];
    const isBuildDistroEnabled = useSelector((state: RootState) => selectIsBuildDistroEnabled(state, appId));
    const shouldDefaultToBuildDistro = useSelector(
        (state: RootState) => selectAppSettings(state, appId)?.shouldDefaultToBuildDistro ?? false,
    );
    const appPlatform = useAppPlatform({ orgId, appId });
    const appImageUrl = useSelector((state: RootState) => selectAppSettings(state, appId)?.appImageUrl);

    const isPlatformBuildDistroSupported = getIsBuildDistroSupportedPlatform(appPlatform);

    const availableOptions =
        isPlatformBuildDistroSupported ? [NavigationTab.Builds, NavigationTab.Releases] : [NavigationTab.Releases];

    const hasBothOptions = availableOptions.length === 2;

    const defaultSelection = shouldDefaultToBuildDistro ? NavigationTab.Builds : NavigationTab.Releases;

    const [selectedTab, setSelectedTab] = React.useState<NavigationTab>(routeBasedTab || defaultSelection);

    React.useEffect(() => {
        setSelectedTab(routeBasedTab || defaultSelection);
    }, [routeBasedTab, defaultSelection]);

    const activeTab = hasBothOptions ? selectedTab : availableOptions[0];

    const sortedBuckets = React.useMemo(() => [...appBuckets].sort(buildBucketSort), [appBuckets]);

    const handleTabChange = (value: string | number) => {
        const newTab = value as NavigationTab;
        setSelectedTab(newTab);
        history.push(routeForNavigationTab(newTab, orgId, appId), { [ROUTER_REDIRECT]: true });
    };

    const renderBuildsContent = () => {
        if (!isBuildDistroEnabled || sortedBuckets.length === 0) {
            return null;
        }

        return (
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    paddingTop: 16,
                    paddingBottom: 30,
                }}
            >
                {sortedBuckets.map((bucket) => {
                    if (bucket.status === BucketStatus.Archived) {
                        return null;
                    }

                    return (
                        <MenuItem
                            icon={null}
                            key={bucket.id}
                            isSelected={selectedSubOption === bucket.id}
                            isChildMenuItem={true}
                            onClick={() => {
                                history.push(createAppBuildDistroBucketRoute(orgId, appId, bucket.id));
                            }}
                            title={
                                <div style={menuItemContainerStyle}>
                                    <BuildBucketAvatar appImageUrl={appImageUrl} size={12} bucket={bucket} />
                                    <Text style={menuItemTextStyle}>{bucket.name}</Text>
                                    {(bucket.orgWideAccessEnabled || bucket.publicBucketLinkEnabled) && (
                                        <BucketAccessIconTooltip
                                            bucketHasOrgWideAccess={bucket.orgWideAccessEnabled}
                                            isExternalBucketView={bucket.publicBucketLinkEnabled}
                                        />
                                    )}
                                </div>
                            }
                        />
                    );
                })}
                <MenuItem
                    icon={null}
                    key={SUB_OPTION_SHARED_WITH_ME}
                    isSelected={selectedSubOption === SUB_OPTION_SHARED_WITH_ME}
                    isChildMenuItem={true}
                    onClick={() => {
                        history.push(createAppSharedWithMeBucketRoute(orgId, appId));
                    }}
                    title={
                        <div style={menuItemContainerStyle}>
                            <BlockOutlined style={menuItemIconStyle} />
                            <Text style={menuItemTextStyle}>Shared with me</Text>
                        </div>
                    }
                />
            </div>
        );
    };

    const renderReleasesContent = () => {
        return (
            <div>
                <div
                    style={{
                        borderTop: `0.5px solid ${sc.border}`,
                        borderBottom: `0.5px solid ${sc.border}`,
                    }}
                >
                    <MenuItem
                        icon={null}
                        key={SUB_OPTION_RELEASE_CALENDAR}
                        isSelected={selectedSubOption === SUB_OPTION_RELEASE_CALENDAR}
                        isChildMenuItem={true}
                        onClick={() => {
                            history.push(createAppReleaseCalendarRoute(orgId, appId));
                        }}
                        title={
                            <div style={{ ...menuItemContainerStyle, gap: 20, paddingLeft: 0 }}>
                                <CalendarOutlined
                                    style={{
                                        ...menuItemIconStyle,
                                        color:
                                            selectedSubOption === SUB_OPTION_RELEASE_CALENDAR ? sc.textMuted : sc.icon,
                                    }}
                                />
                                <Text style={{ ...menuItemTextStyle, color: sc.text }}>Release schedule</Text>
                            </div>
                        }
                    />
                </div>
                <Releases key={appId} appId={appId} orgId={orgId} sidebarColors={sc} sidebarTheme={sidebarTheme} />
            </div>
        );
    };

    return (
        <div
            style={{
                width: '100%',
                flexShrink: 0,
            }}
        >
            {hasBothOptions && !isMobile && (
                <div style={{ padding: '20px 8px' }}>
                    <NavigationSegmented
                        value={activeTab}
                        onChange={handleTabChange}
                        options={[
                            {
                                value: NavigationTab.Releases,
                                icon: <AppstoreAddOutlined />,
                                label: displayTextForNavigationTab(NavigationTab.Releases),
                            },
                            {
                                value: NavigationTab.Builds,
                                icon: <MobileOutlined />,
                                label: displayTextForNavigationTab(NavigationTab.Builds),
                            },
                        ]}
                    />
                </div>
            )}

            {activeTab === NavigationTab.Builds && renderBuildsContent()}
            {activeTab === NavigationTab.Releases && renderReleasesContent()}
        </div>
    );
};
